import { useCallback, useEffect, useState } from 'react';

import { createApiHook } from '~/hooks/utils/createApiHook';
import { TransferListResponse, TransferRepository } from '~/repositories/Transfer';
import { UserTransferFilterSnapshot } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';

export type TransferSnapshotFilters = {
  amount?: string | null;
  amountGte?: string | null;
  amountLte?: string | null;
  bankAccountId?: string | null;
  createdAtGte?: string | null;
  createdAtLt?: string | null;
  isIncoming?: boolean | null;
  status?: string | null;
  type?: string | null;
};

const ALL_FILTERS = [
  'amount',
  'amountGte',
  'amountLte',
  'bankAccountId',
  'createdAtGte',
  'createdAtLt',
  'isIncoming',
  'status',
  'type',
];

interface UseTransferSnapshotsReturn {
  transferSnapshots: UserTransferFilterSnapshot[];
  fetchTransferSnapshots: () => Promise<void>;
  createTransferSnapshot: (name: string, filters: TransferSnapshotFilters) => Promise<void>;
  updateTransferSnapshot: (index: number, name: string, filters: TransferSnapshotFilters) => Promise<void>;
  deleteTransferSnapshot: (index: number) => Promise<void>;
  isLoading: boolean;
}

const isEmpty = (value: string | boolean | null | undefined) => value === null || value === undefined || value === '';

const cleanFilters = (filters: TransferSnapshotFilters, omitEmpty: boolean = true) => {
  const parsedFilters = {
    ...filters,
    amount: filters?.amount && Number(filters.amount) > 0 ? String(filters.amount) : undefined,
    amountGte: filters?.amountGte && Number(filters.amountGte) > 0 ? String(filters.amountGte) : undefined,
    amountLte: filters?.amountLte && Number(filters.amountLte) > 0 ? String(filters.amountLte) : undefined,
  };

  return Object.fromEntries(
    ALL_FILTERS.filter((filter) =>
      omitEmpty ? !isEmpty(parsedFilters[filter as keyof TransferSnapshotFilters]) : true
    ).map((filter) => [
      filter,
      !isEmpty(parsedFilters[filter as keyof TransferSnapshotFilters])
        ? parsedFilters[filter as keyof TransferSnapshotFilters]
        : null,
    ])
  );
};

export const useTransferSnapshot = createApiHook<TransferListResponse, TransferSnapshotFilters & { limit: number }>(
  (filters) => TransferRepository.getAll({ ...cleanFilters(filters), limit: filters.limit }),
  {
    triggerAutomatically: true,
    triggerOnSessionStoreSubscribe: true,
  }
);

export const useTransferSnapshots = (): UseTransferSnapshotsReturn => {
  const { currentUser } = useSessionStore();
  const [isLoading, setIsLoading] = useState(true);
  const [transferSnapshots, setTransferSnapshots] = useState<UserTransferFilterSnapshot[]>([]);

  const fetchTransferSnapshots = useCallback(async () => {
    if (!currentUser?.getPlatformSettings?.transferSnapshots?.length) {
      setTransferSnapshots([]);

      setIsLoading(false);

      return;
    }

    try {
      const sortedTransferSnapshots = [...currentUser.getPlatformSettings.transferSnapshots]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((entry) => ({
          ...entry,
          filters: cleanFilters(entry.filters ?? {}, false),
        }));

      setTransferSnapshots(sortedTransferSnapshots);
    } catch (err) {
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, currentUser?.getPlatformSettings?.transferSnapshots]);

  useEffect(() => {
    fetchTransferSnapshots();
  }, [currentUser?.getPlatformSettings?.transferSnapshots]);

  const createTransferSnapshot = useCallback(
    async (name: string, filters: TransferSnapshotFilters) => {
      try {
        setTransferSnapshots((prevSnapshots) => {
          const highestOrder = prevSnapshots.reduce(
            (max, snapshot) => (snapshot.order !== undefined && snapshot.order > max ? snapshot.order : max),
            -1
          );

          const newSnapshotObj = {
            name,
            filters,
            order: highestOrder + 1,
          };

          const updatedSnapshots = [...prevSnapshots, newSnapshotObj];

          currentUser
            ?.updatePlatformSettings({
              transferSnapshots: updatedSnapshots.map((entry) => ({
                ...entry,
                filters: cleanFilters(entry.filters ?? {}),
              })),
            })
            .catch((err) => {
              fetchTransferSnapshots();
              throw err;
            });

          return updatedSnapshots;
        });
      } catch (err) {
        throw err;
      }
    },
    [currentUser, fetchTransferSnapshots, setTransferSnapshots]
  );

  const updateTransferSnapshot = useCallback(
    async (index: number, name: string, filters: TransferSnapshotFilters) => {
      try {
        setTransferSnapshots((prevSnapshots) => {
          const updatedSnapshots = [...prevSnapshots];

          if (index < 0 || index >= updatedSnapshots.length) {
            throw new Error('Invalid snapshot index');
          }

          const updatedSnapshotObj = {
            ...updatedSnapshots[index],
            name,
            filters,
          };
          updatedSnapshots[index] = updatedSnapshotObj;

          currentUser
            ?.updatePlatformSettings({
              transferSnapshots: updatedSnapshots.map((entry) => ({
                ...entry,
                filters: cleanFilters(entry.filters ?? {}),
              })),
            })
            .catch((err) => {
              fetchTransferSnapshots();
              throw err;
            });

          return updatedSnapshots;
        });
      } catch (err) {
        throw err;
      }
    },
    [currentUser, fetchTransferSnapshots, setTransferSnapshots]
  );

  const deleteTransferSnapshot = useCallback(
    async (index: number) => {
      try {
        setTransferSnapshots((prevSnapshots) => {
          const updatedSnapshots = [...prevSnapshots];

          if (index < 0 || index >= updatedSnapshots.length) {
            throw new Error('Invalid snapshot index');
          }

          updatedSnapshots.splice(index, 1);

          currentUser
            ?.updatePlatformSettings({
              transferSnapshots: updatedSnapshots,
            })
            .catch((err) => {
              fetchTransferSnapshots();
              throw err;
            });

          return updatedSnapshots;
        });
      } catch (err) {
        throw err;
      }
    },
    [currentUser, fetchTransferSnapshots, setTransferSnapshots]
  );

  return {
    transferSnapshots,
    fetchTransferSnapshots,
    createTransferSnapshot,
    updateTransferSnapshot,
    deleteTransferSnapshot,
    isLoading,
  };
};
